// AnnualAssessment/index.tsx
import React, { useState, useEffect } from 'react';
import { Steps, Button, Card, Row, Col, Typography, Divider } from 'antd';
import { useNavigate } from 'react-router-dom';
import { useForm, FormProvider, Control, FieldErrors } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useFetchUserProfileQuery } from '@/store/apis/userApi';

import { displaySuccess, displayError } from '@/utils/displayMessageUtils';

import { ClientInfoStep } from './components/PersonalDetailsStep';
import { OtherIncomeStep } from './components/OtherIncomeStep';
import { PropertyIncomeStep } from './components/PropertyIncomeStep';
import { FuturePlansStep } from './components/FuturePlansStep';
import { ReviewNotesStep } from './components/ReviewNotesStep';

import { DEFAULT_PRELODGMENT_CHECKLIST_VALUES } from './form/defaultValues';
import { fieldsForIndex, STEP_ORDER, STEP_FIELDS } from './form/stepFields';
import { preLodgementChecklistSchema } from './validation/preLodgementChecklist.schema';
import {
  PreLodgementChecklistFormValues,
  PreLodgementChecklistStepKey,
} from './types/preLodgementChecklist';
import { useSubmitAnnualAssessmentMutation } from '@/pages/tax/apis/annualAssessmentAPI';
import { useGetPersonalPropertyListQuery } from '@/pages/mortgage/apis/propertyAPI';

type StepProps = {
  control: Control<PreLodgementChecklistFormValues>;
  error: FieldErrors<PreLodgementChecklistFormValues>;
};

export default function PreLodgementChecklist() {
  const [current, setCurrent] = useState(0);
  const navigate = useNavigate();

  const methods = useForm<PreLodgementChecklistFormValues>({
    defaultValues: DEFAULT_PRELODGMENT_CHECKLIST_VALUES,
    resolver: yupResolver(preLodgementChecklistSchema),
    mode: 'onSubmit',
  });

  const { data: profileData } = useFetchUserProfileQuery();
  const [submitAnnualAssessment] = useSubmitAnnualAssessmentMutation();

  const { data: propertyData } = useGetPersonalPropertyListQuery({
    property_location: 'australian',
  });

  const {
    handleSubmit,
    trigger,
    formState: { isSubmitting, errors },
    control,
  } = methods;

  console.log('errors', errors);

  const { setValue } = methods;

  useEffect(() => {
    const profile = profileData?.data?.common_data;
    if (!profile) return;

    // Map API fields → form fields
    if (profile.citizenship) {
      setValue('citizenship', profile.citizenship);
    }
    if (profile.incomeSource) {
      setValue('incomeSource', profile.incomeSource);
    }
    if (profile?.taxResidencyStatus) {
      setValue('taxResidencyStatus', profile.taxResidencyStatus);
    }
    // employer doesn't have a clean equivalent — leave blank for user to fill
  }, [profileData, setValue]);

  const STEP_CONFIG: Record<
    PreLodgementChecklistStepKey,
    { title: string; Component: React.FC<StepProps> }
  > = {
    personalDetails: {
      title: 'Personal Details',
      Component: ClientInfoStep,
    },
    propertyIncome: {
      title: 'Property Income',
      Component: PropertyIncomeStep,
    },
    otherIncome: {
      title: 'Other Income',
      Component: OtherIncomeStep,
    },
    futurePlans: {
      title: 'Future Plans',
      Component: FuturePlansStep,
    },
    reviewNotes: {
      title: 'Additional Details',
      Component: ReviewNotesStep,
    },
  };

  const steps = STEP_ORDER.map((key) => {
    const { title, Component } = STEP_CONFIG[key];
    return {
      key,
      title,
      content: <Component control={control} error={errors} />,
    };
  });

  const next = async () => {
    const ok = await trigger(fieldsForIndex(current));
    if (!ok) return;
    setCurrent((c) => Math.min(c + 1, STEP_ORDER.length - 1));
  };

  const prev = () => setCurrent((c) => Math.max(c - 1, 0));

  // Sidebar click handler: backward jumps are free; forward jumps validate all intermediate steps.
  const handleStepClick = async (targetIndex: number) => {
    if (targetIndex <= current) {
      setCurrent(targetIndex);
      return;
    }
    const fieldsToValidate = STEP_ORDER.slice(0, targetIndex).flatMap(
      (key) => STEP_FIELDS[key] as (keyof PreLodgementChecklistFormValues)[],
    );
    const ok = await trigger(fieldsToValidate);
    if (!ok) return;
    setCurrent(targetIndex);
  };

  const onSubmit = handleSubmit(async (values) => {
    const propertyIds = (propertyData?.data ?? []).map((p: any) => p.id);

    try {
      await submitAnnualAssessment({
        details: values,
        properties: propertyIds,
      }).unwrap();
      displaySuccess('Pre-lodgement checklist submitted');
      navigate('/tax/tax-returns');
    } catch (err) {
      displayError(err);
    }
  });

  return (
    <FormProvider {...methods}>
      <Card
        style={{ width: '100%', marginTop: '20px' }}
        title="Pre-lodgement Checklist"
        extra={
          <Typography.Text type="secondary">
            Step {current + 1} of {steps.length}
          </Typography.Text>
        }
      >
        <Row gutter={[24, 24]}>
          <Col xs={24} md={6}>
            <div
              style={{
                background: '#f8fafc',
                border: '1px solid #e1e1e1',
                borderRadius: 10,
                padding: '20px 16px',
                position: 'sticky',
                top: 24,
              }}
            >
              <Steps
                direction="vertical"
                current={current}
                onChange={handleStepClick}
                size="small"
                items={steps.map((s, index) => {
                  const stepKey = STEP_ORDER[index];
                  const hasError = STEP_FIELDS[stepKey].some(
                    (field) => !!errors[field],
                  );
                  return {
                    key: s.key,
                    title: s.title,
                    ...(hasError && { status: 'error' as const }),
                  };
                })}
              />
            </div>
          </Col>

          <Col xs={24} md={18}>
            <div>{steps[current].content}</div>

            <Divider style={{ margin: '24px 0 16px' }} />

            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <Button disabled={current === 0} onClick={prev} htmlType="button">
                Back
              </Button>

              {current < steps.length - 1 ? (
                <Button type="primary" onClick={next} htmlType="button">
                  Next
                </Button>
              ) : (
                <Button
                  type="primary"
                  htmlType="button"
                  onClick={() => onSubmit()}
                  loading={isSubmitting}
                >
                  Submit
                </Button>
              )}
            </div>
          </Col>
        </Row>
      </Card>
    </FormProvider>
  );
}
